import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DateRangePickerProps } from '@cloudscape-design/components/date-range-picker';
import { ListTicketsParams } from '../api/tickets';

export interface TicketFilters {
  statuses: string[];
  excludeStatuses: string[];
  severities: string[];
  originalSeverities: string[];
  assignee: string;
  dateRange: DateRangePickerProps.Value | null;
  searchText: string;
  searchScope: 'all' | 'ticketId' | 'title' | 'aiSummary';
  ticketIds: string[];
  themeTitle: string;
  /** Theme run (jobId) these tickets were drilled from; used for breadcrumbs. */
  themeRun: string;
}

const EMPTY_FILTERS: TicketFilters = {
  statuses: [],
  excludeStatuses: [],
  severities: [],
  originalSeverities: [],
  assignee: '',
  dateRange: null,
  searchText: '',
  searchScope: 'all',
  ticketIds: [],
  themeTitle: '',
  themeRun: '',
};

export interface TicketFiltersHook {
  filters: TicketFilters;
  setFilter: <K extends keyof TicketFilters>(key: K, value: TicketFilters[K]) => void;
  removeFilter: (key: string, value?: string) => void;
  clearAll: () => void;
  applyPreset: (preset: FilterPreset) => void;
  toApiParams: (page: number, pageSize: number, sortBy: string, sortOrder: string) => ListTicketsParams & { q?: string };
  activeFilterCount: number;
  hasActiveFilters: boolean;
}

export type FilterPreset = 'myTickets' | 'highSev' | 'stale' | 'thisWeek' | 'unassigned';

const PRESETS: Record<FilterPreset, Partial<TicketFilters>> = {
  myTickets: { assignee: 'marehal' },
  highSev: { severities: ['SEV_1', 'SEV_2'] },
  stale: {
    excludeStatuses: ['Resolved', 'Closed'],
  },
  thisWeek: {
    dateRange: { type: 'relative', amount: 7, unit: 'day', key: '7-days' } as DateRangePickerProps.Value,
  },
  unassigned: { assignee: '__unassigned__' },
};

function filtersToParams(params: URLSearchParams, filters: TicketFilters): void {
  if (filters.statuses.length) params.set('statuses', filters.statuses.join(','));
  if (filters.excludeStatuses.length) params.set('excludeStatuses', filters.excludeStatuses.join(','));
  if (filters.severities.length) params.set('severities', filters.severities.join(','));
  if (filters.originalSeverities.length) params.set('originalSeverities', filters.originalSeverities.join(','));
  if (filters.assignee) params.set('assignee', filters.assignee);
  if (filters.searchText) params.set('q', filters.searchText);
  if (filters.searchScope !== 'all') params.set('scope', filters.searchScope);
  if (filters.ticketIds.length) params.set('ticketIds', filters.ticketIds.join(','));
  if (filters.themeTitle) params.set('themeTitle', filters.themeTitle);
  if (filters.themeRun) params.set('themeRun', filters.themeRun);
  if (filters.dateRange) {
    if (filters.dateRange.type === 'relative') {
      params.set('dateRange', `relative:${filters.dateRange.amount}:${filters.dateRange.unit}`);
    } else if (filters.dateRange.type === 'absolute') {
      params.set('dateRange', `absolute:${filters.dateRange.startDate}:${filters.dateRange.endDate}`);
    }
  }
}

function paramsToFilters(params: URLSearchParams): TicketFilters {
  const filters = { ...EMPTY_FILTERS };

  const statuses = params.get('statuses');
  if (statuses) filters.statuses = statuses.split(',');

  const excludeStatuses = params.get('excludeStatuses');
  if (excludeStatuses) filters.excludeStatuses = excludeStatuses.split(',');

  const severities = params.get('severities');
  if (severities) filters.severities = severities.split(',');

  const originalSeverities = params.get('originalSeverities');
  if (originalSeverities) filters.originalSeverities = originalSeverities.split(',');

  const assignee = params.get('assignee');
  if (assignee) filters.assignee = assignee;

  const q = params.get('q');
  if (q) filters.searchText = q;

  const scope = params.get('scope') as TicketFilters['searchScope'] | null;
  if (scope) filters.searchScope = scope;

  const dateRange = params.get('dateRange');
  if (dateRange) {
    const parts = dateRange.split(':');
    if (parts[0] === 'relative') {
      filters.dateRange = { type: 'relative', amount: parseInt(parts[1]), unit: parts[2] as any, key: 'custom' };
    } else if (parts[0] === 'absolute') {
      filters.dateRange = { type: 'absolute', startDate: parts[1], endDate: parts[2] };
    }
  }

  const ticketIds = params.get('ticketIds');
  if (ticketIds) filters.ticketIds = ticketIds.split(',');

  const themeTitle = params.get('themeTitle');
  if (themeTitle) filters.themeTitle = themeTitle;

  const themeRun = params.get('themeRun');
  if (themeRun) filters.themeRun = themeRun;

  return filters;
}

function getDateParams(range: DateRangePickerProps.Value | null): { dateFrom?: string; dateTo?: string } {
  if (!range) return {};
  if (range.type === 'absolute') {
    return { dateFrom: range.startDate, dateTo: range.endDate };
  }
  const now = new Date();
  const start = new Date(now);
  switch (range.unit) {
    case 'day': start.setDate(start.getDate() - range.amount); break;
    case 'week': start.setDate(start.getDate() - range.amount * 7); break;
    case 'month': start.setMonth(start.getMonth() - range.amount); break;
    case 'year': start.setFullYear(start.getFullYear() - range.amount); break;
  }
  return { dateFrom: start.toISOString(), dateTo: now.toISOString() };
}

export function useTicketFilters(): TicketFiltersHook {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<TicketFilters>(() => paramsToFilters(searchParams));

  const syncToUrl = useCallback((newFilters: TicketFilters) => {
    const params = new URLSearchParams();
    filtersToParams(params, newFilters);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const setFilter = useCallback(<K extends keyof TicketFilters>(key: K, value: TicketFilters[K]) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      syncToUrl(next);
      return next;
    });
  }, [syncToUrl]);

  const removeFilter = useCallback((key: string, value?: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      const field = next[key as keyof TicketFilters];
      if (Array.isArray(field) && value) {
        (next as any)[key] = field.filter((v: string) => v !== value);
      } else if (key === 'assignee') {
        next.assignee = '';
      } else if (key === 'dateRange') {
        next.dateRange = null;
      } else if (key === 'searchText') {
        next.searchText = '';
      } else if (key === 'ticketIds') {
        next.ticketIds = [];
        next.themeTitle = '';
        next.themeRun = '';
      } else {
        (next as any)[key] = Array.isArray(field) ? [] : '';
      }
      syncToUrl(next);
      return next;
    });
  }, [syncToUrl]);

  const clearAll = useCallback(() => {
    const next = { ...EMPTY_FILTERS };
    setFilters(next);
    syncToUrl(next);
  }, [syncToUrl]);

  const applyPreset = useCallback((preset: FilterPreset) => {
    const next = { ...EMPTY_FILTERS, ...PRESETS[preset] };
    setFilters(next);
    syncToUrl(next);
  }, [syncToUrl]);

  const toApiParams = useCallback((page: number, pageSize: number, sortBy: string, sortOrder: string) => {
    const params: ListTicketsParams & { q?: string } = {
      sortBy,
      sortOrder,
      page: String(page),
      maxResults: String(pageSize),
    };

    // Support single or comma-separated multi-value status
    if (filters.statuses.length === 1) {
      params.status = filters.statuses[0];
    } else if (filters.statuses.length > 1) {
      params.status = filters.statuses.join(',');
    }

    if (filters.excludeStatuses.length) {
      (params as any).exclude_status = filters.excludeStatuses.join(',');
    }

    if (filters.severities.length === 1) {
      params.severity = filters.severities[0];
    } else if (filters.severities.length > 1) {
      params.severity = filters.severities.join(',');
    }

    if (filters.assignee && filters.assignee !== '__unassigned__') {
      params.assigneeId = filters.assignee;
    } else if (filters.assignee === '__unassigned__') {
      params.assigneeId = '__unassigned__';
    }

    const dateParams = getDateParams(filters.dateRange);
    if (dateParams.dateFrom) params.dateFrom = dateParams.dateFrom;
    if (dateParams.dateTo) params.dateTo = dateParams.dateTo;

    if (filters.searchText) params.q = filters.searchText;

    if (filters.ticketIds.length) {
      (params as any).ticketIds = filters.ticketIds.join(',');
    }

    return params;
  }, [filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.statuses.length) count += filters.statuses.length;
    if (filters.excludeStatuses.length) count += filters.excludeStatuses.length;
    if (filters.severities.length) count += filters.severities.length;
    if (filters.originalSeverities.length) count += filters.originalSeverities.length;
    if (filters.assignee) count += 1;
    if (filters.dateRange) count += 1;
    return count;
  }, [filters]);

  const hasActiveFilters = activeFilterCount > 0 || filters.searchText.length > 0;

  return {
    filters,
    setFilter,
    removeFilter,
    clearAll,
    applyPreset,
    toApiParams,
    activeFilterCount,
    hasActiveFilters,
  };
}
